import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { API, useAuth } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Shield, Users, Save, RefreshCw, RotateCcw, 
  Check, X, AlertTriangle, Eye, Edit, Trash2,
  Send, Download, Info
} from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const roleLabels = {
  ADMIN: { label: 'Administrador', color: 'bg-purple-500', description: 'Acceso total al sistema' },
  ADMINISTRATIVO: { label: 'Administrativo', color: 'bg-blue-500', description: 'Gestión de reportes y catálogos' },
  ENFERMERA: { label: 'Enfermera', color: 'bg-emerald-500', description: 'Registro de vacunaciones' },
  CAJA: { label: 'Caja', color: 'bg-amber-500', description: 'Control de caja diaria' }
};

const actionLabels = {
  view: { label: 'Ver', icon: Eye, color: 'text-blue-600' },
  create: { label: 'Crear', icon: Edit, color: 'text-emerald-600' },
  edit: { label: 'Editar', icon: Edit, color: 'text-amber-600' },
  delete: { label: 'Eliminar', icon: Trash2, color: 'text-red-600' },
  export: { label: 'Exportar', icon: Download, color: 'text-purple-600' },
  send: { label: 'Enviar', icon: Send, color: 'text-green-600' }
};

const RolesAdminPage = () => {
  const { user: currentUser } = useAuth();
  const [roles, setRoles] = useState([]);
  const [resources, setResources] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [editedPermissions, setEditedPermissions] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [resetRole, setResetRole] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rolesRes, resourcesRes] = await Promise.all([
        axios.get(`${API}/roles`, { withCredentials: true }),
        axios.get(`${API}/roles/resources`, { withCredentials: true })
      ]);
      setRoles(rolesRes.data);
      setResources(resourcesRes.data);
      
      // Select first non-admin role by default
      if (!selectedRole && rolesRes.data.length > 0) {
        const defaultRole = rolesRes.data.find(r => r.id !== 'ADMIN') || rolesRes.data[0];
        setSelectedRole(defaultRole.id);
        setEditedPermissions(defaultRole.permissions);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Mount-only initialization; fetchData reads from setters only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectRole = async (roleId) => {
    if (hasChanges) {
      if (!window.confirm('Tienes cambios sin guardar. ¿Deseas continuar?')) {
        return;
      }
    }
    
    setSelectedRole(roleId);
    setHasChanges(false);
    
    try {
      const response = await axios.get(`${API}/roles/${roleId}`, { withCredentials: true });
      setEditedPermissions(response.data.permissions);
    } catch (error) {
      toast.error('Error al cargar el rol');
    }
  };

  const handleTogglePermission = (resource, action) => {
    if (selectedRole === 'ADMIN') {
      toast.error('No puedes modificar los permisos del rol ADMIN');
      return;
    }

    const currentActions = editedPermissions[resource] || [];
    let newActions;
    
    if (currentActions.includes(action)) {
      newActions = currentActions.filter(a => a !== action);
    } else {
      newActions = [...currentActions, action];
    }
    
    setEditedPermissions({
      ...editedPermissions,
      [resource]: newActions
    });
    setHasChanges(true);
  };

  const handleToggleAllResource = (resource, enable) => {
    if (selectedRole === 'ADMIN') {
      toast.error('No puedes modificar los permisos del rol ADMIN');
      return;
    }

    const availableActions = resources[resource]?.actions || [];
    
    setEditedPermissions({
      ...editedPermissions,
      [resource]: enable ? [...availableActions] : []
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (selectedRole === 'ADMIN') {
      toast.error('No puedes modificar los permisos del rol ADMIN');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/roles/${selectedRole}`, editedPermissions, {
        withCredentials: true
      });
      toast.success('Permisos guardados correctamente');
      setHasChanges(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!resetRole) return;
    
    try {
      await axios.post(`${API}/roles/reset/${resetRole}`, {}, {
        withCredentials: true
      });
      toast.success('Permisos restaurados');
      setHasChanges(false);
      fetchData();
      
      if (resetRole === selectedRole) {
        const response = await axios.get(`${API}/roles/${resetRole}`, { withCredentials: true });
        setEditedPermissions(response.data.permissions);
      }
    } catch (error) {
      toast.error('Error al restaurar permisos');
    } finally {
      setResetRole(null);
    }
  };

  const isActionEnabled = (resource, action) => {
    return (editedPermissions[resource] || []).includes(action);
  };

  const getResourcePermissionCount = (resource) => {
    const enabled = (editedPermissions[resource] || []).length;
    const total = resources[resource]?.actions?.length || 0;
    return { enabled, total };
  };

  // Check if current user is admin
  if (currentUser?.role !== 'ADMIN') {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Card className="p-8 text-center">
            <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-amber-500" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Acceso Restringido</h2>
            <p className="text-slate-500">Solo los administradores pueden acceder a esta sección.</p>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <TooltipProvider>
        <div className="space-y-6 animate-fade-in" data-testid="roles-admin-page">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 heading">Administración de Roles</h1>
              <p className="text-slate-500 mt-1">Configura los permisos de cada rol del sistema</p>
            </div>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={fetchData}
                className="gap-2"
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-4 h-4" />
                Actualizar
              </Button>
              {hasChanges && (
                <Button 
                  onClick={handleSave}
                  className="btn-accent gap-2"
                  disabled={saving || selectedRole === 'ADMIN'}
                  data-testid="save-permissions-btn"
                >
                  {saving ? (
                    <div className="loading-spinner w-4 h-4"></div>
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Guardar Cambios
                </Button>
              )}
            </div>
          </div>

          {/* Info Banner */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Matriz de Permisos</p>
                  <p>
                    Selecciona un rol y configura qué acciones puede realizar en cada módulo del sistema.
                    El rol <strong>ADMIN</strong> tiene acceso total y no puede ser modificado.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Roles List */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Roles</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-slate-100">
                    {roles.map((role) => (
                      <button
                        key={role.id}
                        onClick={() => handleSelectRole(role.id)}
                        className={`w-full p-4 text-left hover:bg-slate-50 transition-colors ${
                          selectedRole === role.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                        }`}
                        data-testid={`role-btn-${role.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${roleLabels[role.id]?.color || 'bg-slate-400'}`} />
                          <div>
                            <p className="font-medium text-slate-900">
                              {roleLabels[role.id]?.label || role.id}
                            </p>
                            <p className="text-xs text-slate-500">
                              {roleLabels[role.id]?.description}
                            </p>
                          </div>
                        </div>
                        {role.id === 'ADMIN' && (
                          <Badge className="mt-2 text-xs" variant="outline">
                            <Shield className="w-3 h-3 mr-1" />
                            Protegido
                          </Badge>
                        )}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Reset Button */}
              {selectedRole && selectedRole !== 'ADMIN' && (
                <Button
                  variant="outline"
                  className="w-full mt-4 gap-2 text-amber-600 border-amber-200 hover:bg-amber-50"
                  onClick={() => setResetRole(selectedRole)}
                  data-testid="reset-role-btn"
                >
                  <RotateCcw className="w-4 h-4" />
                  Restaurar Predeterminados
                </Button>
              )}
            </div>

            {/* Permissions Matrix */}
            <div className="lg:col-span-3">
              {loading ? (
                <Card className="p-8">
                  <div className="flex items-center justify-center">
                    <div className="loading-spinner w-8 h-8"></div>
                  </div>
                </Card>
              ) : selectedRole ? (
                <Card data-testid="permissions-matrix">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded-full ${roleLabels[selectedRole]?.color || 'bg-slate-400'}`} />
                          Permisos: {roleLabels[selectedRole]?.label || selectedRole}
                        </CardTitle>
                        <CardDescription>
                          {selectedRole === 'ADMIN' 
                            ? 'El rol ADMIN tiene acceso total y no puede ser modificado'
                            : 'Activa o desactiva permisos para cada módulo'
                          }
                        </CardDescription>
                      </div>
                      {hasChanges && (
                        <Badge className="bg-amber-100 text-amber-700">
                          Cambios sin guardar
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {Object.entries(resources).map(([resourceKey, resourceInfo]) => {
                        const { enabled, total } = getResourcePermissionCount(resourceKey);
                        const allEnabled = enabled === total;
                        const someEnabled = enabled > 0 && enabled < total;
                        
                        return (
                          <div 
                            key={resourceKey}
                            className={`p-4 rounded-lg border ${
                              enabled > 0 ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100'
                            }`}
                            data-testid={`resource-${resourceKey}`}
                          >
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <Switch
                                  checked={allEnabled}
                                  onCheckedChange={(checked) => handleToggleAllResource(resourceKey, checked)}
                                  disabled={selectedRole === 'ADMIN'}
                                  data-testid={`toggle-all-${resourceKey}`}
                                />
                                <div>
                                  <h4 className="font-medium text-slate-900">
                                    {resourceInfo.name}
                                  </h4>
                                  <p className="text-xs text-slate-500">
                                    {resourceInfo.description}
                                  </p>
                                </div>
                              </div>
                              <Badge variant={enabled > 0 ? "default" : "secondary"} className="text-xs">
                                {enabled}/{total}
                              </Badge>
                            </div>
                            
                            <div className="flex flex-wrap gap-2 ml-12">
                              {resourceInfo.actions.map((action) => {
                                const isEnabled = isActionEnabled(resourceKey, action);
                                const ActionIcon = actionLabels[action]?.icon || Eye;
                                
                                return (
                                  <Tooltip key={action}>
                                    <TooltipTrigger asChild>
                                      <button
                                        onClick={() => handleTogglePermission(resourceKey, action)}
                                        disabled={selectedRole === 'ADMIN'}
                                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                                          isEnabled
                                            ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                            : 'bg-slate-100 text-slate-400 hover:bg-slate-200 hover:text-slate-600'
                                        } ${selectedRole === 'ADMIN' ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'}`}
                                        data-testid={`toggle-${resourceKey}-${action}`}
                                      >
                                        <ActionIcon className="w-3.5 h-3.5" />
                                        {actionLabels[action]?.label || action}
                                        {isEnabled ? (
                                          <Check className="w-3.5 h-3.5 text-emerald-600" />
                                        ) : (
                                          <X className="w-3.5 h-3.5" />
                                        )}
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>
                                        {isEnabled ? 'Desactivar' : 'Activar'} permiso de {actionLabels[action]?.label?.toLowerCase() || action}
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="p-8 text-center">
                  <Shield className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">Selecciona un rol para ver sus permisos</p>
                </Card>
              )}
            </div>
          </div>

          {/* Legend */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Leyenda de Acciones</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                {Object.entries(actionLabels).map(([action, info]) => {
                  const Icon = info.icon;
                  return (
                    <div key={action} className="flex items-center gap-2 text-sm">
                      <Icon className={`w-4 h-4 ${info.color}`} />
                      <span className="text-slate-600">{info.label}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Reset Dialog */}
          <AlertDialog open={!!resetRole} onOpenChange={() => setResetRole(null)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Restaurar permisos predeterminados?</AlertDialogTitle>
                <AlertDialogDescription>
                  Se restaurarán los permisos del rol "{roleLabels[resetRole]?.label || resetRole}" 
                  a sus valores predeterminados. Esta acción no se puede deshacer.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleReset}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  Restaurar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </TooltipProvider>
    </Layout>
  );
};

export default RolesAdminPage;
